import { useEffect, type ReactNode } from 'react';
import { useScenarioAssumptionsExport } from 'lib/hooks/useScenarioAssumptionsExport';
import { downloadFromUrl } from 'utils/download-blob';
import { useExportLifecycleToast } from 'lib/hooks/useExportLifecycleToast';

// Nudge this toast below the content-export toast ONLY when that one is actually showing (both
// portal to the same fixed spot). Applied conditionally via `stackBelow` so a lone toast doesn't
// float with an empty gap above it. (A general N-toast stack across the page's other toast sites
// is a separate UI concern; this coordinates only the two export toasts the Actions menu owns.)
const TOAST_STACK_OFFSET_PX = 96;

/** What {@link useScenarioAssumptionsExportFlow} returns — mirrors {@link useAriesMdbExportFlow}. */
export interface ScenarioAssumptionsExportFlow {
  /** Enqueues the export: POST → background job → Pusher completion → download. */
  start: () => void;
  /** True while the background job is in flight — disables the menu item. */
  isExporting: boolean;
  /**
   * The lifecycle toast node: a success toast that self-dismisses after 10s (start + completion),
   * or an error toast that stands until the viewer dismisses it. Null when idle.
   */
  toast: ReactNode;
}

/**
 * Encapsulates the "Export Scenario Assumptions" action for the new /eval3 Actions menu (mock §1):
 * enqueues the scenario-assumptions XLSX for the selected calculation and, on completion, downloads
 * it via the app helper (same tab, no popup — `autoDownload: false` so the browser doesn't block a
 * non-gesture `window.open`), while a shared lifecycle toast reports progress.
 *
 * The workbook resolves the scenarios that fed the run from `resolved_scenario_ids`, so this works
 * for both case-mode and legacy evals (INS-12392) — the legacy button only read the flat
 * `<domain>_scenario_id` keys and rendered every tab empty for a fresh case eval.
 *
 * A run is keyed to the calc's Pusher channel (`eval_calculator_<id>`), so switching evals mid-export
 * drops that subscription. We reset the in-flight state on a `calculationId` change rather than wedge
 * the menu item at isExporting=true forever — mirroring `useAriesMdbExportFlow`'s anti-bleed teardown.
 * `start` is only reachable while a calc is selected, so the `calculationId: null` case never enqueues.
 *
 * @param {number | null} calculationId - The selected EvaluationCalculation id, or null off an eval route.
 * @param {object} [opts] - Rendering options.
 * @param {boolean} [opts.stackBelow] - Offset the toast below a concurrently-showing sibling toast
 *   (the caller knows when the content-export toast is up). Off by default (top slot, no gap).
 * @return {ScenarioAssumptionsExportFlow} The menu trigger, in-flight flag, and toast node.
 */
export function useScenarioAssumptionsExportFlow(
  calculationId: number | null,
  { stackBelow = false }: { stackBelow?: boolean } = {}
): ScenarioAssumptionsExportFlow {
  const { exportStatus, startExport, isExporting, resetExport } =
    useScenarioAssumptionsExport({ calculationId, autoDownload: false });

  const toast = useExportLifecycleToast(
    exportStatus.status,
    exportStatus.error,
    {
      processing:
        'Scenario assumptions export started — your download will begin when it’s ready.',
      complete: 'Your scenario assumptions export is ready.',
    },
    {
      // Same-tab download via the app helper (no new-tab popup); the shared toast reports it.
      // A completion with no URL can't download — surface an error toast rather than a "ready"
      // toast that silently downloads nothing.
      onComplete: () => {
        if (exportStatus.downloadUrl) {
          downloadFromUrl(exportStatus.downloadUrl);
          return;
        }
        return {
          kind: 'error' as const,
          message: 'Export completed but no download link was returned.',
        };
      },
      // Reset (below) clears a settled run's toast when the eval changes; but if a run was
      // still IN FLIGHT, surface that it was abandoned rather than silently wiping it.
      clearOnIdle: true,
      cancelledMessage:
        'Scenario assumptions export cancelled — you switched evals before it finished.',
      // Sit below the content-export toast only when that one is actually showing.
      stackOffset: stackBelow ? TOAST_STACK_OFFSET_PX : undefined,
    }
  );

  // Selecting a different eval re-keys the Pusher channel, dropping the prior run's subscription;
  // clear the stale in-flight state so the menu item never wedges at isExporting=true.
  useEffect(() => {
    resetExport();
  }, [calculationId, resetExport]);

  // `startExport` already gets a fresh identity every render (its exportFn is inline upstream),
  // so wrapping it in useCallback would memoize nothing — pass a plain arrow through.
  return { start: () => void startExport(), isExporting, toast };
}
